import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  tags?: string[];
  noindex?: boolean;
}

export const SEOHead = ({
  title,
  description,
  canonical,
  image = '/logo-125x125.png',
  type = 'website',
  publishedTime,
  modifiedTime,
  tags = [],
  noindex = false,
}: SEOHeadProps) => {
  const location = useLocation();
  // Normalize canonical URL: remove trailing slash for consistency
  const normalizedPath = (canonical || location.pathname).replace(/\/$/, '') || '/';
  const fullUrl = normalizedPath === '/'
    ? 'https://comparebestbehavioralemr.com/'
    : `https://comparebestbehavioralemr.com${normalizedPath}`;
  const fullImageUrl = image.startsWith('http') ? image : `https://comparebestbehavioralemr.com${image}`;

  const robotsContent = noindex
    ? 'noindex, follow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  const isArticle = type === 'article';

  const articleJsonLd = isArticle
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description,
        image: fullImageUrl,
        datePublished: publishedTime,
        dateModified: modifiedTime || publishedTime,
        author: {
          '@type': 'Organization',
          name: 'EMR Software Hub',
        },
        publisher: {
          '@type': 'Organization',
          name: 'EMR Software Hub',
          logo: {
            '@type': 'ImageObject',
            url: 'https://comparebestbehavioralemr.com/logo-125x125.png',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': fullUrl,
        },
        keywords: tags.join(', '),
      }
    : null;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robotsContent} />

      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="EMR Software Hub" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />

      {/* Article-specific */}
      {isArticle && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {isArticle && (modifiedTime || publishedTime) && (
        <meta property="article:modified_time" content={modifiedTime || publishedTime} />
      )}
      {isArticle && tags?.map((tag, i) => (
        <meta property="article:tag" content={tag} key={`article-tag-${i}-${tag}`} />
      ))}

      {/* Structured data */}
      {isArticle && articleJsonLd && (
        <script id="structured-data-article" type="application/ld+json">
          {JSON.stringify(articleJsonLd)}
        </script>
      )}
    </Helmet>
  );
};
